import React from 'react'
import { Helmet } from 'react-helmet';
import AppLayoutTitle from './appLayoutTitle';
import ComTable from '../components/comTable';
import Swal from 'sweetalert2';
import { handleException } from '../../helper/helperError';
import { apiGet, apiPost } from '../../helper/helperApi';


const _MAX_MEMBERS_LIMIT = 20;


const AppOrgTokens = () => {
    const [dataset, setDataset] = React.useState(undefined);
    const [invitations, setInvitations] = React.useState(undefined);

    const fetchData = async () => {
        try {
            apiGet("/members")
                .then((res) => { setDataset(res.data || []); })
                .catch((ex) => handleException(ex, "Failed to fetch members data"));
        } catch (ex) {
            handleException(ex, "Failed to fetch members data!");
        }
    }

    const fetchInvitations = async () => {
        try {
            apiGet("/members/invitations")
                .then((res) => { setInvitations(res.data || []); })
                .catch((ex) => handleException(ex, "Failed to fetch invitations data"));
        } catch (ex) {
            handleException(ex, "Failed to fetch invitations data!");
        }
    }

    React.useEffect(() => {
        fetchData();
        fetchInvitations();
    }, []);

    const handleAddMember = async () => {
        Swal.fire({
            title: 'Add Member/Admin',
            html: `
      <div style="text-align: left; font-size: 14px;">
        <p>Enter the email address of the person you want to add as a member/admin to your dashboard:</p>
      </div>
      <input id="swal-input-email" type="email" class="swal2-input" placeholder="someone@example.com">
    `,
            width: "480px",
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonText: 'Send Invitation',
            showLoaderOnConfirm: true,
            allowOutsideClick: () => !Swal.isLoading(),
            customClass: {
                confirmButton: 'btn-gold text-dark',
                cancelButton: 'btn-light'
            },
            preConfirm: async () => {
                const email = document.getElementById('swal-input-email').value?.trim();
                if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                    Swal.showValidationMessage('Please enter a valid email address.');
                    return;
                }
                try {
                    await apiPost('/members/add', { email });
                    return email;
                } catch (ex) {
                    Swal.showValidationMessage(ex?.response?.data?.error || ex?.message || 'Failed to send invitation.');
                    return;
                }
            }
        }).then(result => {
            if (result.isConfirmed && result.value) {
                Swal.fire({
                    title: '✅ Invitation Sent',
                    html: `An invitation has been sent to <b>${result.value}</b>.`,
                    icon: 'success',
                    timer: 1500,
                });
                fetchData();
            }
        });
    };

    const handleDelete = async (row) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel',
            customClass: {
                confirmButton: 'btn-gold btn-danger text-dark',
                cancelButton: 'btn-light'
            },
            preConfirm: async () => {
                try {
                    const res = await apiPost(`/members/${row.id}/delete`);
                    return res;
                } catch (ex) {
                    Swal.showValidationMessage(ex?.response?.data?.error || ex?.message || 'Failed to delete member.');
                    fetchData();
                    return;
                }
            }
        }).then(result => {
            if (result.isConfirmed && result.value) {
                const message = result.value?.data?.message || 'Member deleted successfully.';
                Swal.fire({
                    title: 'Deleted!',
                    text: message,
                    icon: 'success',
                    timer: 1500,
                });
                fetchData();
            }
        });
    }

    const handleReinvite = async (row) => {
        if (!row || !row.id) return handleException(null, "Invalid member data");
        Swal.fire({
            title: 'Reinvite Member/Admin',
            html: `
                <div style="text-align: left; font-size: 14px;">
                    <p>Are you sure you want to reinvite <b>${row.username}</b>?</p>
                </div>
            `,
            width: "480px",
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonText: 'Reinvite',
            showLoaderOnConfirm: true,
            allowOutsideClick: () => !Swal.isLoading(),
            customClass: {
                confirmButton: 'btn-gold text-dark',
                cancelButton: 'btn-light'
            },
            preConfirm: async () => {
                try {
                    const res = await apiPost(`/members/${row.id}/reinvite`);
                    return res;
                } catch (ex) {
                    Swal.showValidationMessage(ex?.response?.data?.error || ex?.message || 'Failed to reinvite member.');
                    fetchData();
                    return;
                }
            }
        }).then(result => {
            if (result.isConfirmed && result.value) {
                const message = result.value?.data?.message || 'Reinvite successful.';
                Swal.fire({
                    title: '✅ Reinvite Sent',
                    text: message,
                    icon: 'success',
                    timer: 1500,
                });
                fetchData();
            }
        });
    }

    const handleAccept = async (row) => {
        if (!row || !row.id) return handleException(null, "Invalid invitation data");
        Swal.fire({
            title: 'Accept Invitation',
            html: `
                <div style="text-align: left; font-size: 14px;">
                    <p>Are you sure you want to accept the invitation from <b>${row.username}</b>?</p>
                </div>
            `,
            width: "480px",
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonText: 'Accept',
            showLoaderOnConfirm: true,
            allowOutsideClick: () => !Swal.isLoading(),
            customClass: {
                confirmButton: 'btn-gold text-dark',
                cancelButton: 'btn-light'
            },
            preConfirm: async () => {
                try {
                    const res = await apiPost(`/members/invitations/${row.id}/accept`);
                    return res;
                } catch (ex) {
                    Swal.showValidationMessage(ex?.response?.data?.error || ex?.message || 'Failed to accept invitation.');
                    fetchInvitations();
                    return;
                }
            }
        }).then(result => {
            if (result.isConfirmed && result.value) {
                const message = result.value?.data?.message || 'Invitation accepted successfully.';
                Swal.fire({
                    title: '✅ Accepted',
                    text: message,
                    icon: 'success',
                    timer: 1500,
                });
                fetchInvitations();
            }
        });
    }

    const handleDecline = async (row) => {
        if (!row || !row.id) return handleException(null, "Invalid invitation data");
        Swal.fire({
            title: 'Decline Invitation',
            html: `
                <div style="text-align: left; font-size: 14px;">
                    <p>Are you sure you want to decline the invitation from <b>${row.username}</b>?</p>
                </div>
            `,
            width: "480px",
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonText: 'Decline',
            showLoaderOnConfirm: true,
            allowOutsideClick: () => !Swal.isLoading(),
            customClass: {
                confirmButton: 'btn-gold text-dark',
                cancelButton: 'btn-light'
            },
            preConfirm: async () => {
                try {
                    const res = await apiPost(`/members/invitations/${row.id}/decline`);
                    return res;
                } catch (ex) {
                    Swal.showValidationMessage(ex?.response?.data?.error || ex?.message || 'Failed to decline invitation.');
                    return;
                }
            }
        }).then(result => {
            if (result.isConfirmed && result.value) {
                const message = result.value?.data?.message || 'Invitation declined successfully.';
                Swal.fire({
                    title: 'Declined!',
                    text: message,
                    icon: 'success',
                    timer: 1500,
                });
                fetchInvitations();
            }
        });
    }

    const handleStop = async (row) => {
        if (!row || !row.id) return handleException(null, "Invalid invitation data");
        Swal.fire({
            title: 'Stop Invitation',
            html: `
                <div style="text-align: left; font-size: 14px;">
                    <p>Are you sure you want to stop the invitation from <b>${row.username}</b> permanently?</p>
                </div>
            `,
            width: "480px",
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonText: 'Stop',
            showLoaderOnConfirm: true,
            allowOutsideClick: () => !Swal.isLoading(),
            customClass: {
                confirmButton: 'btn-gold text-dark',
                cancelButton: 'btn-light'
            },
            preConfirm: async () => {
                try {
                    const res = await apiPost(`/members/invitations/${row.id}/stop`);
                    return res;
                } catch (ex) {
                    Swal.showValidationMessage(ex?.response?.data?.error || ex?.message || 'Failed to stop invitation.');
                    return;
                }
            }
        }).then(result => {
            if (result.isConfirmed && result.value) {
                const message = result.value?.data?.message || 'Invitation stopped successfully.';
                Swal.fire({
                    title: 'Stopped!',
                    text: message,
                    icon: 'success',
                    timer: 1500,
                });
                fetchInvitations();
            }
        });
    }


    return <>
        <Helmet>
            <title>Members | Sparkling Email</title>
        </Helmet>
        <>
            <AppLayoutTitle title="Members" buttons={
                <button className="btn btn-gold" onClick={handleAddMember}>Invite Member/Admin</button>
            } />
            <div className="px-3 py-3">
                <div>
                    You can invite trusted individuals to join your dashboard as members and collaborate within your organization.
                </div>
                <div>
                    All members will have the same read-only access as you, including visibility into
                    domains, email templates, reports, and system activity. There are no permission
                    levels — if you don't want someone to see your organization’s data, do not invite
                    them. You can invite up to {_MAX_MEMBERS_LIMIT} members in total, including pending
                    invitations. Once a member accepts the invitation, they will appear in your list. While
                    you cannot delete members after they join, you can disable their access at any time
                    to prevent further visibility.
                </div>
                <br />
                <ComTable
                    data={dataset}
                    isUndefinedIsLoading={true}
                    handleDelete={(row) => handleDelete(row)}
                    handleReinvite={(row) => handleReinvite(row)}
                />
                {invitations && invitations.length > 0 && <>
                    <br /><hr /><br />
                    <h4>Invitations</h4>
                    <div>
                        The Invitations section shows a list of incoming requests from other users
                        who have invited you to join their organization dashboard.
                        Unlike the Members section — where you manage people you’ve invited — this
                        area displays invitations you have received. Each invitation includes the
                        name or email of the requester and the time the request was sent. You can
                        choose to accept or decline each invitation. Once accepted, you'll gain
                        read-only access to that organization’s domains, templates, and activity.
                        Invitations remain pending until you respond, and you can revisit this
                        section at any time to manage outstanding requests.
                    </div>
                    <br />
                    <ComTable
                        data={invitations}
                        isUndefinedIsLoading={true}
                        handleAccept={(row) => handleAccept(row)}
                        handleDecline={(row) => handleDecline(row)}
                        handleStop={(row) => handleStop(row)}
                    />
                </>}
            </div>
        </>
    </>
}
export default AppOrgTokens;